import { SpokePageSpinner } from '@doltech/core/lib/auth/SpokePageSpinner';
import { encryptData } from '@doltech/core/lib/encrypt-page-data/encryptData';
import { components } from '@doltech/pageManagement/dto';
import {
  fetchCommonDataForLandingBlog,
  fetchPublishedPagesWithFilter,
  fetchRedirectPage,
  getCommonDataPreRendering,
} from '@doltech/ui/lib/figma/Layout/Landing/api/pages-landing.m2m';
import { LandingSharedLayoutProps } from '@doltech/ui/lib/figma/Layout/Landing/hocs/withLandingSharedLayout';
import {
  retainBlogLinkOnlyForLinearThinkingBlog,
  retainOnlyUrlOfStaticPageLink,
} from '@doltech/ui/lib/hocs/retainOnlyUrlOfStaticPageLink';
import { useLandingJsonLdSchema } from '@doltech/ui/lib/hooks/useLandingJsonLdSchema';
import { getErrorInfo, printNetworkError } from '@doltech/utils/lib/error';
import { fixRedirectUrl } from '@doltech/utils/lib/url';
import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/router';
import * as React from 'react';
import Head from 'next/head';
import { environment } from '@doltech/core/lib/environment/environment';
import {
  replaceDolVnToLandingBlogMainSite,
  replaceLandingBlogCanonicalUrl,
} from '@doltech/ui/lib/shared/components/SlugPage/replaceLandingBlogCanonicalUrl';
import { PageTypeEnum, PageTypeForSampleAsPagePath } from '@doltech/utils/lib/constants';
import objectMapper from 'object-mapper';
import { bootstrapAuthManager } from '../bootstrapSSRAuthManager';
import { filterDataByPageType } from '../routers/filterDataByPageType';
import urlInfoMapping from '../routers/mapping/url-info-mapping.json';
import { getDataPreRendering } from '../routers/route';
import { PageTemplate } from '../routers/route.template';
import { useBuildSeoMeta } from '@doltech/ui/lib/shared/components/SlugPage/buildSeoMeta';

export interface SlugPageProps extends LandingSharedLayoutProps {
  urlInfo: components['schemas']['PageResponseDTOObject'];
  data?: any;
  error?: any;
  slug?: any;
  structuralSeo?: any;
  schemaScriptString?: string;
  schemaBlogPosting?: string;
  isStructuralSEO?: boolean;
}

const SlugPageError = (error) => (
  <div>
    <pre>Response error: {JSON.stringify(error, null, 2)}</pre>
  </div>
);

const FallbackPage = (props: SlugPageProps) => {
  const { urlInfo, error } = props;
  const { isFallback, reload } = useRouter();

  React.useEffect(() => {
    if (!urlInfo && process.env.NODE_ENV === 'development') {
      reload();
    }
  }, [urlInfo, reload]);

  if (error) {
    return <SlugPageError error={error} />;
  }

  if (isFallback) {
    return (
      <div>
        <h1>Page not found</h1>
        <p>You may need to refresh this page, we need sometime to generate this page...</p>
      </div>
    );
  }

  if (!urlInfo) {
    return <SpokePageSpinner />;
  }

  return <SlugPage {...props} />;
};
//

const buildSEOInfo = ({ urlInfo }) => {
  const structuralSeo = urlInfo?.metadata || '';
  return {
    structuralSeo,
    schemaScriptString: urlInfo?.google?.schemaHtml || '',
    schemaBlogPosting: urlInfo?.blogPosting?.schemaScript || '',
  };
};

const hardCodeSeoMetaData = ({ urlInfo }) => `
<meta property="og:type" content="article" key="og:type">
<meta property="og:locale" content="vi_VN">
<meta property="og:image:type" content="image/jpeg">
<meta property="og:image:alt" content="${urlInfo.dol?.title}">
<meta name="twitter:site" content="@EnglishDol">
${
  urlInfo?.type === PageTypeEnum.DAILY_LEARNING_NEWS || !environment.production
    ? '<meta name="robots" content="noindex,nofollow">'
    : ''
}
`;

export const SlugPage = (props: SlugPageProps) => {
  const { structuralSeo, schemaScriptString, schemaBlogPosting, urlInfo, data, slug, ...rest } =
    props;
  const defaultLdSchema = useLandingJsonLdSchema({ urlInfo, type: 'blog-detail' });

  const { schemaBlogPostingScript, seoMetadata, schemaScript, error } = useBuildSeoMeta({
    hardCodeHeadTags: hardCodeSeoMetaData({ urlInfo }),
    structuralSeo,
    schemaScriptString,
    schemaBlogPosting,
    parseLocalSeoMedataOption: {
      replace: replaceLandingBlogCanonicalUrl,
    },
  });

  const allProps = React.useMemo(
    () => ({
      seoData: urlInfo,
      ...data,
      ...rest,
    }),
    [data, rest, urlInfo]
  );

  if (error) {
    return <SlugPageError error={getErrorInfo(error)} />;
  }
  return (
    <>
      <Head>
        {seoMetadata}
        {schemaScript}
        {schemaBlogPostingScript}
        {defaultLdSchema}
      </Head>
      <PageTemplate pageType={urlInfo?.type} pageProps={allProps} />
    </>
  );
};

export default FallbackPage;

const getPageMatchRedirectUrl = (pages, url) => {
  return pages.find((page) =>
    page.redirectUrls?.some((item) => item.replace(/\//g, '') === url.replace(/\//g, ''))
  );
};

const redirectPageFromPageTypes = ({ pageTypes, pageType, redirectToDomain, path }) => {
  if (pageTypes.includes(pageType)) {
    const redirectTo = fixRedirectUrl(`${redirectToDomain}/${path}`);
    console.log('redirectPageFromPageTypes', redirectTo);
    return {
      redirect: {
        destination: redirectTo,
        statusCode: 301,
      },
    };
  }
};

export const getServerSideProps = async (context) => {
  bootstrapAuthManager();
  const { req, params } = context;
  context.res.setHeader('Cache-Control', 'public, max-age=10, stale-while-revalidate=60');

  const slug = params.slug.join('/').replace(/([^:])(\/\/+)/g, '$1/');

  if (slug === '404') {
    return {
      props: {},
    };
  }

  // we'll check if any url in system match 100% with requested url
  // if no url matched => check if it's a structural url
  const slugUrl = `/${params.slug.join('/')}`.replace(/([^:])(\/\/+)/g, '$1/');
  let urlInfo;
  let data = {};
  let filterUrls = [];
  let commonData = {};
  let redirectPage;

  Sentry.setTag('slug_url', slugUrl);

  const checkRedirectUrl = await fetchRedirectPage({ url: `/blog${req.url}`, app: 'DOL_ENGLISH' });
  if (checkRedirectUrl?.targetUrl && checkRedirectUrl?.httpCode) {
    const redirectTo = fixRedirectUrl(checkRedirectUrl?.targetUrl);
    const statusCode = Number(checkRedirectUrl?.httpCode);
    console.log('seo-redirect-detected:', redirectTo, statusCode);
    return {
      redirect: {
        destination: redirectTo,
        statusCode,
      },
    };
  }

  try {
    const slugUrlFull = `/blog${slugUrl}`;

    filterUrls = await fetchPublishedPagesWithFilter({ url: slugUrlFull });
    urlInfo = filterUrls.find(
      (item) => item.url.replace(/\//g, '') === slugUrlFull.replace(/\//g, '')
    );

    const redirectToDictionaryPage = redirectPageFromPageTypes({
      pageType: urlInfo?.type,
      pageTypes: PageTypeForSampleAsPagePath,
      redirectToDomain: environment.SEO_PREFIX,
      path: slugUrlFull,
    });

    if (redirectToDictionaryPage) {
      return redirectToDictionaryPage;
    }
    redirectPage = getPageMatchRedirectUrl(filterUrls, slugUrlFull);
    if (redirectPage && redirectPage?.url !== slugUrlFull) {
      console.info('redirect to', redirectPage);
      return {
        redirect: {
          destination: fixRedirectUrl(redirectPage.url),
          statusCode: 301,
          basePath: false,
        },
      };
    }
  } catch (ex) {
    if (ex.request || ex.response) {
      printNetworkError(ex, 'compact');
      Sentry.setExtra('error', ex);
      Sentry.captureException(ex);
      Sentry.flush(2000);
      if (process.env.IS_RUN_TIME_ON_DOCKER) {
        throw ex;
      }
    } else {
      console.error('[Error] getStaticProps error:', ex);
      Sentry.setExtra('error', ex);
      Sentry.captureException(ex);
      Sentry.flush(2000);
      throw ex;
    }
  }

  let cmsData = {};
  try {
    // get data for each page - why not use react-query? -> because SEO
    data = filterDataByPageType(await getDataPreRendering(urlInfo), urlInfo?.type);
    commonData = await retainBlogLinkOnlyForLinearThinkingBlog(
      await retainOnlyUrlOfStaticPageLink(await getCommonDataPreRendering()),
      urlInfo?.type
    );
    cmsData = await fetchCommonDataForLandingBlog();
  } catch (ex) {
    if (ex.request || ex.response) {
      printNetworkError(ex, 'compact');
      Sentry.setExtra('error', ex);
      Sentry.captureException(ex);
      Sentry.flush(2000);
      if (process.env.IS_RUN_TIME_ON_DOCKER) {
        throw ex;
      }
    } else {
      console.error('[Error] getDataPreRendering error:', ex);
      Sentry.setExtra('error', ex);
      Sentry.captureException(ex);
      Sentry.flush(2000);
      throw ex;
    }
  }

  const notFound = Boolean(urlInfo) === false;

  if (notFound) {
    return {
      redirect: {
        destination: '/404',
        statusCode: 302,
      },
    };
  }
  const finalUrlInfo = JSON.parse(replaceDolVnToLandingBlogMainSite(JSON.stringify(urlInfo)));

  const { structuralSeo, schemaScriptString, schemaBlogPosting } = buildSEOInfo({
    urlInfo: finalUrlInfo,
  });

  return {
    props: encryptData({
      key: slugUrl,
      urlInfo: objectMapper(finalUrlInfo, urlInfoMapping),
      structuralSeo,
      schemaScriptString,
      schemaBlogPosting,
      data: {
        ...commonData,
        ...data,
        cmsData,
      },
      slug,
    }),
  };
};
